/*
 * Girgit — playful companion mascot.
 * Phase 1: walking, cursor tracking, click-for-tip, right-click menu (hide/send/palette/feed),
 * drag, localStorage persistence, event bus for future phases (P2 props, P3 costumes, P4 reactions).
 *
 * Hard rules (do not break in later phases):
 *   - z-index < any modal/toast (uses 40; app dialogs use 50+)
 *   - Never intercepts clicks outside himself (`pointer-events` scoped to his figure)
 *   - Respects `prefers-reduced-motion`
 *   - Persists all state per-user id in localStorage
 *   - `window.dispatchEvent(new CustomEvent('girgit:cheer'))` etc → reactive triggers for later phases
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import './girgit.css';

const LS = (uid, key) => `girgit_${uid || 'anon'}_${key}`;

const PALETTES = {
  pink:     { light: '#fbcfe8', mid: '#ec4899', dark: '#9d174d' },
  amber:    { light: '#fde68a', mid: '#f59e0b', dark: '#78350f' },
  emerald:  { light: '#a7f3d0', mid: '#10b981', dark: '#065f46' },
  sky:      { light: '#bae6fd', mid: '#38bdf8', dark: '#075985' },
  fuchsia:  { light: '#e9d5ff', mid: '#a855f7', dark: '#581c87' },
};

const TIPS = [
  "Long-press or right-click me for options.",
  "Feed me a fly — open my menu → 🍽.",
  "Drag me anywhere. I'll settle where you drop me.",
  "I change colours. Try the palette in my menu.",
  "Hide me for an hour if I'm in the way — I'll be back.",
  "Point at a fly and I'll snap my tongue at it.",
  "Small breaks make big work. Blink with me.",
  "Pssst — I cheer when the team wins together.",
];

export default function Girgit({ userId, role }) {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const uid = userId || 'anon';

  // Visibility state ---------------------------------------------------------
  const initialHiddenUntil = (() => {
    const v = localStorage.getItem(LS(uid, 'hidden_until'));
    if (!v) return null;
    return new Date(v) > new Date() ? v : null;
  })();
  const [hiddenUntil, setHiddenUntil] = useState(initialHiddenUntil);
  const [sentAway, setSentAway] = useState(sessionStorage.getItem(LS(uid, 'sent_away')) === '1');
  const [offForever, setOffForever] = useState(localStorage.getItem(LS(uid, 'off_forever')) === '1');
  const isHidden = offForever || sentAway || (hiddenUntil && new Date(hiddenUntil) > new Date());

  // Vertical safety padding so Girgit doesn't sit under the mobile bottom nav (h-16 + safe-area).
  const bottomReserve = () => (window.matchMedia('(max-width: 640px)').matches ? 96 : 20);

  // Palette + position -------------------------------------------------------
  const [palette, setPalette] = useState(localStorage.getItem(LS(uid, 'palette')) || 'pink');
  const [pos, setPos] = useState(() => {
    const s = 90;
    const maxY = window.innerHeight - s - bottomReserve();
    try {
      const saved = JSON.parse(localStorage.getItem(LS(uid, 'pos')) || 'null');
      if (saved && typeof saved.x === 'number') {
        // Clamp any stale position into the current viewport (handles rotation / mobile switch)
        return {
          x: Math.max(0, Math.min(window.innerWidth - s, saved.x)),
          y: Math.max(0, Math.min(maxY, saved.y)),
        };
      }
    } catch { /* noop */ }
    return { x: 40, y: Math.max(80, maxY) };
  });
  useEffect(() => { localStorage.setItem(LS(uid, 'pos'), JSON.stringify(pos)); }, [pos, uid]);
  useEffect(() => { localStorage.setItem(LS(uid, 'palette'), palette); }, [palette, uid]);

  // Re-clamp on viewport resize / rotate --------------------------------------
  useEffect(() => {
    const onResize = () => {
      const s = 90;
      const maxY = window.innerHeight - s - bottomReserve();
      setPos(p => ({
        x: Math.max(0, Math.min(window.innerWidth - s, p.x)),
        y: Math.max(0, Math.min(maxY, p.y)),
      }));
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  // Movement -----------------------------------------------------------------
  const [facing, setFacing] = useState(1); // 1 right, -1 left
  const [mood, setMood] = useState('idle'); // idle | walk | wink | worry | sleep | eat
  const targetX = useRef(pos.x);
  const dragging = useRef(false);

  useEffect(() => {
    if (isHidden || reduce) return;
    let t;
    const scheduleWalk = () => {
      const delay = 4000 + Math.random() * 8000;
      t = setTimeout(() => {
        if (dragging.current) { scheduleWalk(); return; }
        const nextX = Math.max(30, Math.min(window.innerWidth - 110, pos.x + (Math.random() * 300 - 150)));
        targetX.current = nextX;
        setFacing(nextX > pos.x ? 1 : -1);
        setMood('walk');
        scheduleWalk();
      }, delay);
    };
    scheduleWalk();
    return () => clearTimeout(t);
  }, [pos.x, isHidden, reduce]);

  // Walking loop
  useEffect(() => {
    if (isHidden) return;
    let raf;
    const tick = () => {
      if (!dragging.current) {
        setPos(p => {
          const dx = targetX.current - p.x;
          if (Math.abs(dx) < 1) {
            if (mood === 'walk') setMood('idle');
            return p;
          }
          return { ...p, x: p.x + Math.sign(dx) * Math.min(0.6, Math.abs(dx)) };
        });
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [mood, isHidden]);

  // Cursor eye tracking ------------------------------------------------------
  const pupilRef = useRef(null);
  useEffect(() => {
    const onMove = (e) => {
      if (!pupilRef.current) return;
      const rect = pupilRef.current.parentElement.getBoundingClientRect();
      const cx = rect.left + rect.width / 2, cy = rect.top + rect.height / 2;
      const dx = e.clientX - cx, dy = e.clientY - cy;
      const dist = Math.min(4, Math.hypot(dx, dy) / 40);
      const a = Math.atan2(dy, dx);
      pupilRef.current.style.transform = `translate(${Math.cos(a) * dist}px, ${Math.sin(a) * dist}px)`;
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  // Size (smaller on mobile) ------------------------------------------------
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 640px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  const SIZE = isMobile ? 68 : 90;

  // Drag (mouse + touch) + long-press for menu -------------------------------
  const longPressTimer = useRef(null);
  const dragMoved = useRef(false);

  const startDrag = (e) => {
    const isTouch = e.type === 'touchstart';
    const point = isTouch ? e.touches[0] : e;
    if (!isTouch) e.preventDefault();
    dragging.current = true;
    dragMoved.current = false;
    const startX = point.clientX, startY = point.clientY;
    const origX = pos.x, origY = pos.y;

    // Long-press → open menu (mobile-friendly alternative to right-click)
    longPressTimer.current = setTimeout(() => {
      longPressTimer.current = null;
      if (!dragMoved.current) {
        setMenu({ x: Math.min(startX, window.innerWidth - 240), y: Math.max(20, startY - 220) });
        // Cancel drag so menu can be interacted with
        dragging.current = false;
        cleanup();
      }
    }, 480);

    const onMove = (ev) => {
      const p = isTouch ? ev.touches[0] : ev;
      const dx = p.clientX - startX, dy = p.clientY - startY;
      if (!dragMoved.current && Math.hypot(dx, dy) > 6) {
        dragMoved.current = true;
        if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
      }
      if (dragMoved.current) {
        if (isTouch) ev.preventDefault();
        setPos({
          x: Math.max(0, Math.min(window.innerWidth - SIZE, origX + dx)),
          y: Math.max(0, Math.min(window.innerHeight - SIZE - bottomReserve(), origY + dy)),
        });
      }
    };
    const onUp = () => {
      if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
      dragging.current = false;
      cleanup();
    };
    const cleanup = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
      window.removeEventListener('touchcancel', onUp);
    };
    if (isTouch) {
      window.addEventListener('touchmove', onMove, { passive: false });
      window.addEventListener('touchend', onUp);
      window.addEventListener('touchcancel', onUp);
    } else {
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    }
  };

  // Interactions -------------------------------------------------------------
  const [tip, setTip] = useState(null);
  const catchFlyRef = useRef(null);
  const handleClick = () => {
    if (dragMoved.current) { dragMoved.current = false; return; }
    // If a fly is out there, tapping Girgit makes him snap at it (mobile-friendly, no floating button)
    if (catchFlyRef.current && catchFlyRef.current()) return;
    setMood('wink');
    setTip(TIPS[Math.floor(Math.random() * TIPS.length)]);
    setTimeout(() => setMood('idle'), 900);
    setTimeout(() => setTip(null), 3400);
  };

  // Right-click / long-press menu --------------------------------------------
  const [menu, setMenu] = useState(null);
  const menuRef = useRef(null);
  const openMenu = (e) => {
    e.preventDefault();
    const x = Math.min(e.clientX, window.innerWidth - 240);
    const y = Math.min(e.clientY, window.innerHeight - 340);
    setMenu({ x, y: Math.max(10, y) });
  };
  const closeMenu = () => setMenu(null);
  useEffect(() => {
    if (!menu) return;
    const outside = (e) => {
      // Ignore taps/clicks that land inside the menu itself — let the item's onClick fire
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setMenu(null);
    };
    const t = setTimeout(() => {
      window.addEventListener('mousedown', outside);
      window.addEventListener('touchstart', outside, { passive: true });
    }, 0);
    return () => {
      clearTimeout(t);
      window.removeEventListener('mousedown', outside);
      window.removeEventListener('touchstart', outside);
    };
  }, [menu]);

  const hideForHours = (hrs) => {
    const until = new Date(Date.now() + hrs * 3600000).toISOString();
    localStorage.setItem(LS(uid, 'hidden_until'), until);
    setHiddenUntil(until);
    closeMenu();
  };
  const sendAway = () => {
    sessionStorage.setItem(LS(uid, 'sent_away'), '1');
    setSentAway(true);
    closeMenu();
  };
  const turnOff = () => {
    if (!window.confirm('Turn Girgit off forever? You can re-enable him from your profile.')) return;
    localStorage.setItem(LS(uid, 'off_forever'), '1');
    setOffForever(true);
    closeMenu();
  };
  const pickPalette = (name) => { setPalette(name); closeMenu(); };

  // Fly-catch (Phase 2 — kept minimal, wired to event bus) -------------------
  const [fly, setFly] = useState(null);
  const [tongue, setTongue] = useState(null);
  const spawnFly = useCallback(() => {
    const startY = 80 + Math.random() * (window.innerHeight * 0.5);
    const startX = 40 + Math.random() * (window.innerWidth - 80);
    setFly({ x: startX, y: startY, vx: (Math.random() - .5) * 1.6, vy: (Math.random() - .5) * 1.2 });
  }, []);
  useEffect(() => {
    if (!fly) return;
    let raf;
    const tick = () => {
      setFly(f => {
        if (!f) return f;
        let vx = f.vx + (Math.random() - .5) * .3, vy = f.vy + (Math.random() - .5) * .3;
        vx = Math.max(-2, Math.min(2, vx)); vy = Math.max(-1.5, Math.min(1.5, vy));
        let x = f.x + vx, y = f.y + vy;
        if (x < 20 || x > window.innerWidth - 20) vx = -vx;
        if (y < 40 || y > window.innerHeight * 0.7) vy = -vy;
        return { x, y, vx, vy };
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [fly]);
  const catchFly = () => {
    if (!fly) return false;
    const mouthX = pos.x + (facing === 1 ? 78 : 12), mouthY = pos.y + 40;
    const angle = Math.atan2(fly.y - mouthY, fly.x - mouthX) * 180 / Math.PI;
    const len = Math.hypot(fly.x - mouthX, fly.y - mouthY);
    setTongue({ x: mouthX, y: mouthY, angle, length: 0 });
    setMood('eat');
    setTimeout(() => setTongue(t => t && { ...t, length: len }), 10);
    setTimeout(() => { setFly(null); setTongue(t => t && { ...t, length: 0 }); }, 260);
    setTimeout(() => { setTongue(null); setMood('idle'); }, 550);
    return true;
  };
  // Keep the ref pointing at the latest closure so `handleClick` above can trigger it
  catchFlyRef.current = catchFly;

  // Event bus for later phases ----------------------------------------------
  useEffect(() => {
    const fireConfetti = async (opts = {}) => {
      if (reduce) return;
      try {
        const mod = await import('canvas-confetti');
        const confetti = mod.default || mod;
        const originX = (pos.x + SIZE / 2) / window.innerWidth;
        const originY = (pos.y + SIZE / 2) / window.innerHeight;
        confetti({
          particleCount: opts.big ? 90 : 40,
          spread: 70,
          startVelocity: 35,
          origin: { x: Math.min(0.95, Math.max(0.05, originX)), y: Math.min(0.9, Math.max(0.1, originY)) },
          colors: ['#ec4899','#f59e0b','#10b981','#38bdf8','#a855f7','#fbbf24'],
          scalar: 0.85,
          disableForReducedMotion: true,
        });
      } catch { /* confetti optional */ }
    };
    const onCheer = (e) => {
      setMood('cheer');
      fireConfetti({ big: !!(e && e.detail && e.detail.big) });
      setTimeout(() => setMood('idle'), 1800);
    };
    const onWorry = () => { setMood('worry'); setTimeout(() => setMood('idle'), 1400); };
    window.addEventListener('girgit:cheer', onCheer);
    window.addEventListener('girgit:worry', onWorry);
    return () => {
      window.removeEventListener('girgit:cheer', onCheer);
      window.removeEventListener('girgit:worry', onWorry);
    };
  }, [pos.x, pos.y, SIZE, reduce]);

  // Auto-hide over modals ----------------------------------------------------
  const [dimmed, setDimmed] = useState(false);
  useEffect(() => {
    const check = () => {
      const hasModal = document.querySelector('[role="dialog"], [data-testid$="-backdrop"]');
      setDimmed(!!hasModal);
    };
    const obs = new MutationObserver(check);
    obs.observe(document.body, { childList: true, subtree: true });
    check();
    return () => obs.disconnect();
  }, []);

  if (isHidden) return null;
  const p = PALETTES[palette] || PALETTES.pink;
  const winking = mood === 'wink' || mood === 'cheer';
  const cheering = mood === 'cheer';

  return (
    <>
      <div
        className={`girgit-root ${dimmed ? 'dimmed' : ''} ${reduce ? 'still' : ''} ${cheering ? 'cheering' : ''}`}
        style={{ left: pos.x, top: pos.y, width: SIZE, height: SIZE, transform: `scaleX(${facing})` }}
        onMouseDown={startDrag}
        onTouchStart={startDrag}
        onClick={(e) => { e.stopPropagation(); handleClick(); }}
        onContextMenu={openMenu}
        data-testid="girgit-mascot"
        aria-label="Girgit the chameleon — long-press or right-click for options"
      >
        <svg viewBox="0 0 100 100" width={SIZE} height={SIZE} style={{ overflow: 'visible' }}>
          <defs>
            <radialGradient id={`gr-${palette}`} cx=".35" cy=".3" r=".8">
              <stop offset="0" stopColor={p.light}/>
              <stop offset=".55" stopColor={p.mid}/>
              <stop offset="1" stopColor={p.dark}/>
            </radialGradient>
          </defs>
          <ellipse cx="50" cy="86" rx="34" ry="4" fill="#000" opacity=".25"/>
          <path d="M20 60 Q10 40 30 32 Q50 22 68 26 Q85 30 87 46 Q88 62 74 70 L28 72 Q20 72 20 60Z" fill={`url(#gr-${palette})`} stroke={p.dark} strokeWidth="1.2"/>
          <path d="M20 60 Q8 60 6 50 Q4 40 14 38 Q22 38 22 46 Q22 52 18 52" fill={`url(#gr-${palette})`} stroke={p.dark} strokeWidth="1.2"/>
          <path d="M28 32 L31 27 L34 32 L37 26 L40 32 L43 26 L46 32 L50 25 L54 32 L58 26 L62 32 L66 27 L70 32 L74 27 L78 33" fill="none" stroke={p.dark} strokeWidth="1"/>
          <g stroke={p.dark} strokeWidth="1.6" strokeLinecap="round" fill="none">
            <path d="M36 72 L34 82 M42 72 L44 82 M66 72 L64 82 M72 72 L74 82"/>
          </g>
          <circle cx="76" cy="38" r="9" fill={`url(#gr-${palette})`} stroke={p.dark} strokeWidth="1.2"/>
          <circle cx="76" cy="38" r="6" fill="#fef3c7" stroke={p.dark} strokeWidth=".8"/>
          {winking ? (
            <path d="M72 38 Q76 41 80 38" stroke="#0f172a" strokeWidth="1.4" fill="none" strokeLinecap="round"/>
          ) : (
            <>
              <circle ref={pupilRef} cx="76" cy="38" r="3" fill="#0f172a"/>
              <circle cx="77" cy="37" r="1" fill="#fff"/>
            </>
          )}
          <circle cx="86" cy="52" r=".8" fill={p.dark}/>
          {mood === 'worry' ? (
            <path d="M76 60 Q80 57 84 60" stroke={p.dark} strokeWidth="1" fill="none" strokeLinecap="round"/>
          ) : (mood === 'eat' || cheering) ? (
            <path d="M74 58 Q80 66 86 58" stroke={p.dark} strokeWidth="1.4" fill="none" strokeLinecap="round"/>
          ) : (
            <path d="M78 60 Q80 62 82 60" stroke={p.dark} strokeWidth="1" fill="none" strokeLinecap="round"/>
          )}
          <ellipse cx="68" cy="58" rx="2.5" ry="1.4" fill="#f472b6" opacity=".5"/>

          {/* Party hat when cheering */}
          {cheering && (
            <g className="girgit-hat">
              <polygon points="68,10 84,30 60,30" fill="#ec4899" stroke="#9d174d" strokeWidth="1.2"/>
              <polygon points="68,10 84,30 60,30" fill="url(#hat-stripes)" opacity=".6"/>
              <circle cx="68" cy="10" r="3" fill="#fbbf24" stroke="#78350f" strokeWidth=".8"/>
              <circle cx="66" cy="28" r="1.2" fill="#38bdf8"/>
              <circle cx="76" cy="26" r="1.2" fill="#10b981"/>
              <circle cx="72" cy="30" r="1.2" fill="#a855f7"/>
              <defs>
                <pattern id="hat-stripes" width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(35)">
                  <rect width="2" height="4" fill="#fff"/>
                </pattern>
              </defs>
            </g>
          )}
        </svg>
        {tip && <div className="girgit-tip" style={{ transform: `scaleX(${facing})` }}>{tip}</div>}
        {cheering && <div className="girgit-cheer-emoji" style={{ transform: `scaleX(${facing})` }}>🎉</div>}
      </div>

      {fly && <div className="girgit-fly" style={{ left: fly.x, top: fly.y }}>🪰</div>}
      {tongue && (
        <div className="girgit-tongue" style={{
          left: tongue.x, top: tongue.y,
          width: tongue.length, transform: `rotate(${tongue.angle}deg)`,
        }}/>
      )}
      {fly && !tongue && (
        <div
          className="girgit-catch-hint"
          style={{ left: pos.x + SIZE / 2, top: pos.y - 6 }}
          data-testid="girgit-catch-hint"
        >
          Tap me!
        </div>
      )}

      {menu && (
        <div ref={menuRef} className="girgit-menu" style={{ left: menu.x, top: menu.y }}
             onClick={(e) => e.stopPropagation()}
             onTouchStart={(e) => e.stopPropagation()}
             data-testid="girgit-menu">
          <button onClick={() => { spawnFly(); closeMenu(); }}>🍽 Feed him a fly</button>
          <div className="girgit-menu-sub">
            <span>🎨 Colour</span>
            <div className="girgit-swatches">
              {Object.keys(PALETTES).map(k => (
                <button key={k} className={palette === k ? 'on' : ''} onClick={() => pickPalette(k)} style={{ background: PALETTES[k].mid }} title={k} data-testid={`girgit-palette-${k}`} />
              ))}
            </div>
          </div>
          <hr/>
          <button onClick={() => hideForHours(1)}>😴 Hide 1 hour</button>
          <button onClick={() => hideForHours(24)}>🌙 Hide for the day</button>
          <button onClick={sendAway} data-testid="girgit-send-away">👋 Send him away (returns next visit)</button>
          <hr/>
          <button onClick={turnOff} className="danger">🔕 Turn off forever</button>
        </div>
      )}
    </>
  );
}
